import { ChatSessionMenu } from '@/components/chat/ChatSessionMenu';
import { InlineRenameInput } from '@/components/chat/InlineRenameInput';
import { cn } from '@/lib/cn';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { ChatSession } from '@innermate/shared';
import { useNavigate } from '@tanstack/react-router';
import { MessageSquare } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface Props {
  session: ChatSession;
  activePath: string;
  /** rename 모드 토글. session.id 가 일치하면 인라인 input 으로 전환. */
  renamingId: string | null;
  onStartRename: (id: string) => void;
  onDoneRename: () => void;
  /** 들여쓰기 (프로젝트 안에 들어있는 채팅). */
  indent?: boolean;
}

/**
 * 사이드바의 채팅 한 줄. 클릭 = 라우트 이동, 드래그 = 프로젝트로 이동.
 *
 * 의도적으로 `<Link>` 대신 `<button>` + 명시적 `navigate()` 사용. TanStack Router 의
 * `<Link>` 는 onClick 을 내부 navigation 핸들러와 조합해서 preventDefault 흐름이
 * 라이브러리 버전에 따라 다르게 동작 — drag 종료 후 따라오는 native click 한 번을
 * 안정적으로 차단하려면 navigate 호출 자체를 우리가 컨트롤하는 게 안전.
 */
export function DraggableChatRow({
  session,
  activePath,
  renamingId,
  onStartRename,
  onDoneRename,
  indent,
}: Props) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `session:${session.id}`,
    data: { type: 'session', sessionId: session.id, projectId: session.projectId },
  });
  const to = `/chat/${session.id}`;
  const isActive = activePath === to;
  const isRenaming = renamingId === session.id;

  // 드래그가 한 번이라도 활성화되었으면 그 직후 따라오는 click 은 무시.
  // dnd-kit 은 pointerup 후 native click 을 자동 suppress 하지 않는다.
  const wasDraggingRef = useRef(false);
  useEffect(() => {
    if (isDragging) wasDraggingRef.current = true;
  }, [isDragging]);

  const handleClick = () => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    if (isActive) return;
    navigate({ to });
  };

  const style = transform
    ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : undefined }
    : undefined;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn('group relative', isDragging && 'z-50')}
      data-active={isActive || undefined}
    >
      {isActive ? (
        <span
          className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-full bg-accent"
          aria-hidden
        />
      ) : null}
      {isRenaming ? (
        <div
          className={cn(
            'flex items-center gap-2.5 h-9 rounded-md text-sm',
            indent ? 'pl-7 pr-2.5' : 'px-2.5',
            isActive ? 'bg-surface' : 'bg-surface-muted/40',
          )}
        >
          <MessageSquare className="size-4 shrink-0 text-foreground-muted" />
          <div className="flex-1 min-w-0">
            <InlineRenameInput session={session} onDone={onDoneRename} />
          </div>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleClick}
            {...listeners}
            {...attributes}
            className={cn(
              'flex items-center gap-2.5 h-9 rounded-md text-sm transition-colors w-full text-left pr-8 touch-none',
              indent ? 'pl-7' : 'pl-2.5',
              isActive
                ? 'bg-surface text-foreground'
                : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
            )}
          >
            <MessageSquare className="size-4 shrink-0" />
            <span className="truncate flex-1">{session.title || '새 채팅'}</span>
          </button>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-data-[active]:opacity-100 focus-within:opacity-100 transition-opacity">
            <ChatSessionMenu session={session} onStartRename={() => onStartRename(session.id)} />
          </div>
        </>
      )}
    </li>
  );
}
